import { useEffect, useState } from 'react';
import FlashMessage from '../FlashMessage';
import TeamMemberTable from '../TeamMemberTable'; // ✅ new import
import Button from '../Button';
import {
  createTeamMember,
  fetchTeamMembers,
  deleteTeamMember,
  activateTeam
} from '../../api/api.js';
import Card from '../Card.js';
import { useMemberOptions } from '../../hooks/useMemberOptions.js';
import { useNavigate } from 'react-router-dom';

function TeamMemberBuilder({
  competition,
  team,
  competition_event_id,
  afterActivate
}) {
  const { setterOptions, closerOptions, veteranOptions, rookieOptions, allRepOptions } = useMemberOptions();
  const navigate = useNavigate()
  const [setters, setSetters] = useState([]);
  const [closers, setClosers] = useState([]);
  const [vets, setVets] = useState([]);
  const [rookies, setRookies] = useState([]);
  const [filteredSetterOpts, setFilteredSetterOpts] = useState([]);
  const [filteredCloserOpts, setFilteredCloserOpts] = useState([]);
  const [filteredRookieOpts, setFilteredRookieOpts] = useState([]);
  const [filteredVeteranOpts, setFilteredVeteranOpts] = useState([]);
  const [teamMemberCount, setTeamMemberCount] = useState(0)
  const [allTeamMembers, setAllTeamMembers] = useState([])
  const [disabled, setDisabled] = useState(false);
  const ROOKIEANDVET = 'Rookie & Veteran';
  const SETTERANDCLOSER = 'Setter & Closer';
  const [flashMessage, setFlashMessage] = useState('');
  const [flashType, setFlashType] = useState(''); // 'success' or 'error'
  // Member options


  const removeTeamMember = async (team_member_id) => {
    try {
      await deleteTeamMember(team_member_id);

      setFlashMessage('Team member removed successfully');
      setFlashType('success');

      await getTeamMembers(team.id);
    } catch (err) {
      if (err.status === 422) {
        setFlashMessage('This member has duplicate records in Airtable. Please remove the duplicates in Airtable first, then try again.');
      } else {
        setFlashMessage(err.message || 'Failed to remove team member');
      }
      setFlashType('error');
    }
  };

  const addTeamMember = async (employee_id, r, title, tenure) => {
    let role = ROOKIEANDVET === r ? title : tenure;
    role = (role === 'ROOKIE' || role === 'SOPHOMORE') && ROOKIEANDVET === r ? 'Rookie' : 'Veteran';

    try {
      await createTeamMember({
        employee_id: employee_id,
        competition_team_id: team.id,
        competition_event_id: competition_event_id,
        role: role
      });

      setFlashMessage('Team member added successfully');
      setFlashType('success');

      await getTeamMembers(team.id);
    } catch (err) {
      setFlashMessage(err.message || 'Failed to add team member');
      setFlashType('error');
    }
  };

  const getTeamMembers = async (team_id) => {
    const tms = await fetchTeamMembers(team_id);
    setTeamMemberCount(tms.length);
    setAllTeamMembers(tms);

    setSetters([]);
    setClosers([]);
    setRookies([]);
    setVets([]);

    tms.forEach((tm) => {
      if (tm.role === 'Setter') {
        setSetters((prev) => [...prev, tm]);
      } else if (tm.role === 'Closer') {
        setClosers((prev) => [...prev, tm]);
      } else if (tm.role === 'Veteran') {
        setVets((prev) => [...prev, tm]);
      } else if (tm.role === 'Rookie') {
        setRookies((prev) => [...prev, tm]);
      }
    });
  };

  // Initial fetch — runs immediately on mount so existing members show before options load
  useEffect(() => {
    if (!team?.id) return;
    getTeamMembers(team.id).catch(err => {
      setFlashMessage(err.message || 'Failed to fetch team members');
      setFlashType('error');
    });
  }, []);

  // Recompute filtered options whenever base options or team members change
  useEffect(() => {
    let setter = setterOptions;
    let closer = closerOptions;
    let rookie = rookieOptions;
    let veteran = veteranOptions;

    if (allTeamMembers.length > 0 && allRepOptions.length > 0) {
      const firstMember = allTeamMembers[0];
      const firstRep = allRepOptions.find(r => String(r.employee_id) === String(firstMember.employee_id));

      if (competition.division_restriction && firstRep) {
        const division = firstRep.division_name ?? null;
        setter = setter.filter(o => (o.division_name ?? null) === division);
        closer = closer.filter(o => (o.division_name ?? null) === division);
        rookie = rookie.filter(o => (o.division_name ?? null) === division);
        veteran = veteran.filter(o => (o.division_name ?? null) === division);
      }

      if (competition.partner_restriction && firstRep) {
        const partnership = firstRep.partnership_name ?? null;
        setter = setter.filter(o => (o.partnership_name ?? null) === partnership);
        closer = closer.filter(o => (o.partnership_name ?? null) === partnership);
        rookie = rookie.filter(o => (o.partnership_name ?? null) === partnership);
        veteran = veteran.filter(o => (o.partnership_name ?? null) === partnership);
      }
    }

    setFilteredSetterOpts(setter);
    setFilteredCloserOpts(closer);
    setFilteredRookieOpts(rookie);
    setFilteredVeteranOpts(veteran);
  }, [setterOptions, closerOptions, rookieOptions, veteranOptions, allTeamMembers, allRepOptions]);

  const attemptToActivateTeam = async () => {
    setDisabled(true);
    try {
      await activateTeam(team.id);
      afterActivate()
    } catch (err) {
      setFlashMessage(err.message || 'Failed to activate team');
      setFlashType('error');
    } finally {
      setDisabled(false);
    }
  };

  return (
    <Card className='gap-3 sm:gap-5'>
      <h2 className='m-0 text-md'>
        {team.name} {competition.office_restriction && `(${team.office_name})`}
      </h2>

      <div className='w-11/12 flex flex-row justify-between items-center'>
        <h3 className='text-md sm:text-lg font-semibold'>Add Team Members</h3>
        <p className="text-xs text-gray-500">{teamMemberCount} of {competition.team_size} Participants added</p>
      </div>

      <FlashMessage
        message={flashMessage}
        type={flashType}
        onClose={() => {
          setFlashMessage('');
          setFlashType('');
        }}
      />

      <div className="w-11/12">
        {competition.division_split ? (
          <TeamMemberTable
            title={SETTERANDCLOSER}
            members={[...setters, ...closers]}
            options={[...filteredSetterOpts, ...filteredCloserOpts]}
            count={(competition.setter_count || 0) + (competition.closer_count || 0)}
            rep_level_count={[{count: competition.setter_count || 0, role: 'Setter', current_count: setters.length}, {count: competition.closer_count || 0, role: 'Closer', current_count: closers.length}]}
            addTeamMember={addTeamMember}
            onRemove={removeTeamMember}
          />
        ) : (
          <TeamMemberTable
            title={ROOKIEANDVET}
            members={[...rookies, ...vets]}
            options={[...filteredRookieOpts, ...filteredVeteranOpts]}
            count={(competition.rookie_count || 0) + (competition.veteran_count || 0)}
            rep_level_count={[{count: competition.rookie_count || 0, role: 'Rookie', current_count: rookies.length}, {count: competition.veteran_count || 0, role: 'Veteran', current_count: vets.length}]}
            addTeamMember={addTeamMember}
            onRemove={removeTeamMember}
          />
        )}
      </div>

      <Button
        color={disabled ? 'gray' : 'yellow'}
        onClickMethod={attemptToActivateTeam}
        disabled={disabled}
      >
        Submit
      </Button>
    </Card>
  );
}

export default TeamMemberBuilder;
